import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/firebaseApp";

import LoadingBouncyDots from "../../components/ui/LoadingBouncyDots";
import Spacer from "../../components/ui/Spacer";
import { ChartBlock } from "../../components/result/ChartBlock";
import MetricTrendChart from "../../components/charts/MetricTrendChart";
import MetricLineChart from "../../components/charts/MetricLineChart";
import MetricBarChart from "../../components/charts/MetricBarChart";
import { LockOverlay } from "../../components/ui/LockOverlay";

import { useExertionTrendData } from "../../hooks/useExertionTrendData";
import { useExertionVentilationCompare } from "../../hooks/useExertionVentilationCompare";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import {
  applySessionColoursToLineDatasets,
  applySessionColoursToBarDatasets,
  buildSessionColours,
} from "../../utils/sessionColours";

const USER_LIMIT = 15;

function normalize(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

/**
 * Mirror your ActivityFeedProvider logic enough that downstream hooks work:
 * - baseName: doc id
 * - ts: Firestore doc field (number ms)
 * - variant: "exertion" etc.
 * - repo: "pdx-recording-exertion-recovery" etc.
 * - data: raw doc data (optional, but handy)
 */
function mapMeasurementDocToFeedItem({ uid, docSnap }) {
  const d = docSnap.data() || {};
  const ts = Number.isFinite(d.ts) ? d.ts : 0;

  return {
    uid,
    baseName: docSnap.id,
    ts, // IMPORTANT: useExertionTrendData looks for it?.ts / it?.data?.ts
    timestamp: ts, // optional compatibility
    variant: d.variant || "", // IMPORTANT: useExertionTrendData filters by variant === "exertion"
    repo: d.repo || null, // IMPORTANT: useExertionTrendData filters by repo
    kind: d.kind || null, // not required for exertion trends, but fine
    data: d, // so it?.data?.repo etc can work
  };
}

function sortNewestFirst(a, b) {
  const ta = Number(a?.ts || a?.timestamp || 0);
  const tb = Number(b?.ts || b?.timestamp || 0);
  return tb - ta;
}

function AdminTrendsPage() {
  const { t } = useTranslation();
  const db = useMemo(() => getFirestore(firebaseApp), []);

  // --- user search + selection ---
  const [emailPrefix, setEmailPrefix] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // --- selected user's measurement index subscription ---
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState(null);
  const [items, setItems] = useState([]);
  const unsubRef = useRef(null);

  const [removed, setRemoved] = useState([]); // indices

  // Debounced user search (matches your other admin pages)
  useEffect(() => {
    let alive = true;

    const handle = setTimeout(async () => {
      try {
        setUsersLoading(true);

        const prefix = normalize(emailPrefix);
        const qRef = prefix
          ? query(
              collection(db, "lungform_users"),
              orderBy("email", "asc"),
              startAt(prefix),
              endAt(prefix + "\uf8ff"),
              limit(USER_LIMIT),
            )
          : query(
              collection(db, "lungform_users"),
              orderBy("email", "asc"),
              limit(USER_LIMIT),
            );

        const snap = await getDocs(qRef);
        if (!alive) return;

        setUsers(snap.docs.map((d) => ({ uid: d.id, ...(d.data() || {}) })));
      } catch (e) {
        if (!alive) return;
        setUsers([]);
      } finally {
        if (alive) setUsersLoading(false);
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [db, emailPrefix]);

  const subscribeToUserMeasurements = useCallback(() => {
    // cleanup prior subscription
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    const uid = selectedUser?.uid;
    if (!uid) {
      setItems([]);
      setFeedLoading(false);
      setFeedError(null);
      return;
    }

    setFeedLoading(true);
    setFeedError(null);

    // IMPORTANT: This is the Firestore index, not Storage:
    // measurements/{uid}/breatheultra (ordered by ts desc)
    const colRef = collection(db, "measurements", uid, "breatheultra");
    const qRef = query(colRef, orderBy("ts", "desc"), limit(200));

    unsubRef.current = onSnapshot(
      qRef,
      (snap) => {
        const next = snap.docs
          .map((docSnap) => mapMeasurementDocToFeedItem({ uid, docSnap }))
          .sort(sortNewestFirst);

        setItems(next);
        setFeedLoading(false);
        setFeedError(null);
      },
      (err) => {
        console.error("[AdminTrendsPage] onSnapshot error", err);
        setItems([]);
        setFeedLoading(false);
        setFeedError(err);
      },
    );
  }, [db, selectedUser?.uid]);

  useEffect(() => {
    subscribeToUserMeasurements();
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [subscribeToUserMeasurements]);

  const hasAnyRecordings =
    !feedLoading && !feedError && (items?.length || 0) > 0;

  // ---- trends data (reuse your existing hooks) ----
  const uid = selectedUser?.uid || null;

  const {
    loading: loadingExertion,
    error: exertionError,
    docs: exertionDocs,
    rrSeries: rr5minSeries,
    br1Series,
    br2Series,
    rt90Series,
    rt95Series,
  } = useExertionTrendData({
    uid,
    items,
    repo: "pdx-recording-exertion-recovery",
    tag: "breatheultra",
    maxDocs: 30,
  });

  const { cumulativeDatasets, bins30s } = useExertionVentilationCompare({
    docs: exertionDocs,
    maxCompare: exertionDocs?.length || 0,
    binSize: 30,
    maxT: 300,
  });

  const primary = getPrimaryColour();

  const sessionColours = useMemo(() => {
    return buildSessionColours((cumulativeDatasets || []).length, primary);
  }, [cumulativeDatasets, primary]);

  const colouredCumulativeDatasets = useMemo(() => {
    return applySessionColoursToLineDatasets(
      cumulativeDatasets,
      sessionColours,
      primary,
    );
  }, [cumulativeDatasets, sessionColours, primary]);

  const colouredBins30s = useMemo(() => {
    return {
      ...bins30s,
      datasets: applySessionColoursToBarDatasets(
        bins30s?.datasets,
        sessionColours,
        primary,
      ),
    };
  }, [bins30s, sessionColours, primary]);

  const lockTrends = false; // admin view: never lock

  return (
    <div className="w-full max-w-6xl p-4 space-y-4">
      <div>
        <h1 className="title-text">Admin · Trends</h1>
        <p className="muted-text">
          Source: <code>measurements/&lt;uid&gt;/breatheultra</code> (Firestore)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left: select user */}
        <div className="md:col-span-5 space-y-3">
          <div className="rounded-2xl border-2 border-greyscale-1 p-3 space-y-2">
            <div className="text-sm font-semibold">Select user</div>

            <input
              className="custom-input-on-dark w-full"
              placeholder="Search user by email prefix…"
              value={emailPrefix}
              onChange={(e) => setEmailPrefix(e.target.value)}
            />

            {usersLoading ? (
              <LoadingBouncyDots text="Searching users…" />
            ) : (
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {users.map((u) => (
                  <button
                    key={u.uid}
                    className={[
                      "w-full text-left rounded-xl p-2 border-2",
                      selectedUser?.uid === u.uid
                        ? "border-primary"
                        : "border-greyscale-1 hover:border-greyscale-2",
                    ].join(" ")}
                    onClick={() =>
                      setSelectedUser({ uid: u.uid, email: u.email || "" })
                    }
                  >
                    <div className="text-sm truncate">{u.email || "—"}</div>
                    <div className="text-xs text-typography/60 font-mono truncate">
                      {u.uid}
                    </div>
                  </button>
                ))}
                {!users.length && (
                  <div className="text-xs text-typography/60">
                    No users found.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border-2 border-greyscale-1 p-3 space-y-2">
            <div className="text-sm font-semibold">Data status</div>
            <div className="text-xs text-typography/60">
              User:{" "}
              <span className="font-mono">{selectedUser?.uid || "—"}</span>
              {" · "}
              Items: <span className="font-mono">{items.length}</span>
            </div>

            <div className="flex justify-end">
              <button
                className="outlined-button"
                onClick={subscribeToUserMeasurements}
                disabled={!selectedUser?.uid}
              >
                Refresh
              </button>
            </div>

            {feedLoading && (
              <LoadingBouncyDots text="Loading measurement index…" />
            )}
            {feedError && (
              <div className="text-xs text-red-400">
                {String(feedError?.message || feedError)}
              </div>
            )}
            {exertionError && (
              <div className="text-xs text-red-400">
                Exertion trends error:{" "}
                {String(exertionError?.message || exertionError)}
              </div>
            )}
          </div>
        </div>

        {/* Right: charts */}
        <div className="md:col-span-7 space-y-3">
          {!selectedUser?.uid ? (
            <div className="rounded-2xl border-2 border-greyscale-1 p-3 text-sm text-typography/70">
              Select a user to render their trends.
            </div>
          ) : !hasAnyRecordings ? (
            <div className="rounded-2xl border-2 border-greyscale-1 p-3 text-sm text-typography/70">
              {feedLoading
                ? "Loading…"
                : "No measurements found for this user."}
            </div>
          ) : (
            <>
              <ChartBlock
                title={t("trends.minuteVentilationCompare.title", {
                  defaultValue: "Ventilation Comparison",
                })}
                infoKey="info.minuteVentilation"
              >
                <MetricLineChart
                  height={400}
                  datasets={colouredCumulativeDatasets || []}
                  preset="none"
                  showLegend={true}
                  showTooltip={false}
                  xType="linear"
                  xMin={0}
                  xMax={300}
                  xTickStep={60}
                  xTickFormat={(v) => `${v}s`}
                  yDecimals={0}
                  xLabel={t("activity.spirometry.timeLabel", {
                    defaultValue: "Time (s)",
                  })}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("trends.minuteVentilationBinsCompare.title", {
                  defaultValue: "Ventilation by time block",
                })}
                infoKey="info.minuteVentilationBins30s"
              >
                <MetricBarChart
                  height={400}
                  labels={colouredBins30s?.labels || []}
                  datasets={colouredBins30s?.datasets || []}
                  showLegend={true}
                  showTooltip={false}
                  xLabel={t("activity.spirometry.timeLabel", {
                    defaultValue: "Time (s)",
                  })}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("trends.rr.title", { defaultValue: "Resp. Rate" })}
                infoKey="info.respiratoryRate"
              >
                <MetricTrendChart
                  height={200}
                  labels={rr5minSeries?.labels || []}
                  values={rr5minSeries?.values || []}
                  yUnit={t("trends.rr.unit", { defaultValue: "breaths/min" })}
                  lineLabel={t("trends.rr.label", {
                    defaultValue: "Resp. Rate",
                  })}
                  loading={feedLoading || loadingExertion}
                  yDecimals={0}
                  removedIndices={removed}
                  onRemovedChange={setRemoved}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("metrics.breathingRecovery60.name", {
                  defaultValue: "Breathing Recovery (1 min)",
                })}
                infoKey="info.breathingRecovery"
              >
                <MetricTrendChart
                  height={200}
                  labels={br1Series?.labels || []}
                  values={br1Series?.values || []}
                  yUnit={t("trends.recovery.unit", {
                    defaultValue: "bpm drop",
                  })}
                  lineLabel={t("metrics.breathingRecovery60.name", {
                    defaultValue: "Breathing Recovery (1 min)",
                  })}
                  loading={feedLoading || loadingExertion}
                  yMin={0}
                  yDecimals={2}
                  removedIndices={removed}
                  onRemovedChange={setRemoved}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("metrics.breathingRecovery120.name", {
                  defaultValue: "Breathing Recovery (2 min)",
                })}
                infoKey="info.breathingRecovery"
              >
                <MetricTrendChart
                  height={200}
                  labels={br2Series?.labels || []}
                  values={br2Series?.values || []}
                  yUnit={t("trends.recovery.unit", {
                    defaultValue: "bpm drop",
                  })}
                  lineLabel={t("metrics.breathingRecovery120.name", {
                    defaultValue: "Breathing Recovery (2 min)",
                  })}
                  loading={feedLoading || loadingExertion}
                  yMin={0}
                  yDecimals={2}
                  removedIndices={removed}
                  onRemovedChange={setRemoved}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("metrics.recoveryTime90.name", {
                  defaultValue: "Recovery Time (90%)",
                })}
                infoKey="info.recoveryTime"
              >
                <MetricTrendChart
                  height={200}
                  labels={rt90Series?.labels || []}
                  values={rt90Series?.values || []}
                  yUnit={t("trends.recTime.unit", { defaultValue: "seconds" })}
                  lineLabel={t("metrics.recoveryTime90.name", {
                    defaultValue: "Recovery Time (90%)",
                  })}
                  loading={feedLoading || loadingExertion}
                  yMin={0}
                  yDecimals={0}
                  yTickStep={30}
                  removedIndices={removed}
                  onRemovedChange={setRemoved}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <ChartBlock
                title={t("metrics.recoveryTime95.name", {
                  defaultValue: "Recovery Time (95%)",
                })}
                infoKey="info.recoveryTime"
              >
                <MetricTrendChart
                  height={200}
                  labels={rt95Series?.labels || []}
                  values={rt95Series?.values || []}
                  yUnit={t("trends.recTime.unit", { defaultValue: "seconds" })}
                  lineLabel={t("metrics.recoveryTime95.name", {
                    defaultValue: "Recovery Time (95%)",
                  })}
                  loading={feedLoading || loadingExertion}
                  yMin={0}
                  yDecimals={0}
                  yTickStep={30}
                  removedIndices={removed}
                  onRemovedChange={setRemoved}
                />
                <LockOverlay show={lockTrends} variant="comingSoon" />
              </ChartBlock>

              <Spacer />

              <details className="rounded-2xl border-2 border-greyscale-1 p-3">
                <summary className="text-sm font-semibold cursor-pointer">
                  Debug: feed items (first 5)
                </summary>
                <pre className="text-xs overflow-x-auto mt-2 whitespace-pre-wrap font-mono">
                  {JSON.stringify(items.slice(0, 5), null, 2)}
                </pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(AdminTrendsPage);
